import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addMachine, getMachines, updateMachine, getMachineDepartments } from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import { Typography, Button, CircularProgress, Stack, TextField, Tooltip, Autocomplete, Checkbox, Card, Grid, FormControlLabel } from "@mui/material";
import { isYear } from "utils/utilFunctions";
import { FileUpload as FileUploadIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AuthorizedImage from "component/AuthorizedImage";

const AddEditMachine = (props) => {
  const dispatch = useDispatch();
  const [imageToUpload, setImageToUpload] = useState("");
  const [formData, setFormData] = useState({
    serialNumber: "",
    manufacturingYear: "",
    maintenanceCode: "",
    assetNumber: "",
    model: "",
    machineType: "",
    criticalMachine: false,
    location: "",
    profileImage: "",
  });

  const handleFileUpload = (selectedFile) => {
    setImageToUpload(selectedFile.target.files[0]);
    // toast.success("Image uploaded successfully!");
  };

  const [errors, setErrors] = useState({});
  const [isEditDisabled, setIsEditDisabled] = useState(false);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.globalState);
  const { machinesData, appConstants, machineDepartments, isLoading } = useSelector((state) => state.service);

  useEffect(() => {
    if (user?.roles?.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_FINANCE)) setIsEditDisabled(true);
  }, []);

  useEffect(() => {
    if (!formData.manufacturer || !appConstants.machineModels) return;
  
    const filteredModels = appConstants.machineModels.filter(
      (item) => item.manufacturer === formData.manufacturer
    );
  
    if (filteredModels.length === 1) {
      setFormData((prev) => ({
        ...prev,
        model: filteredModels[0].model,
      }));
    }
  }, [formData.manufacturer, appConstants.machineModels]);  

  useEffect(() => {
    if (props && props.initialFormData) setFormData(props.initialFormData);
  }, [props.initialFormData]);


  const validations = {
    serialNumber: true,
    location: true,
    model: true,
    machineType: false,
    maintenanceCode: true,
    assetNumber: false,
  };

  const handleSubmit = () => {
    if (!formData || Object.keys(formData).length === 0) {
      return toast.warn("Form data is empty!");
    }

    const formDataToBe = new FormData();

    if (imageToUpload) {
      formDataToBe.append("image", imageToUpload);
    }

    if (formData.manufacturingYear && !isYear(formData.manufacturingYear)) {
      return toast.warn("Please enter a valid year!");
    }

    if (!formData.serialNumber || !formData.model || !formData.maintenanceCode || !formData.location) {
      setErrors({
        serialNumber: formData.serialNumber ? "" : "Please enter serial number!",
        model: formData.model ? "" : "Please enter model!",
        maintenanceCode: formData.maintenanceCode ? "" : "Please enter maintenance code!",
        location: formData.location ? "" : "Please enter location!",
      });
      return toast.warn("Please enter required fields!");
    }

    const selectedDepartment = machineDepartments.find((item) => item.name === formData.location);
    formData.modelId = appConstants.machineModels.find((item) => item.model === formData.model)?.id;

    const maintenanceCodeExists = machinesData.some((machine) => machine.maintenanceCode === formData.maintenanceCode);
    if (maintenanceCodeExists && !formData.id) {
      return toast.error("Maintenance code exists!");
    }

    const serialNumberExists = machinesData.some((item) => item.serialNumber === formData.serialNumber);
    if (serialNumberExists && !formData.id) {
      return toast.error("This data already exists!");
    }

    if (formData.id) {
      formData.machineId = Number(formData.id);
      formData.departmentId = selectedDepartment.id;
      formData.department = selectedDepartment.name;
      delete formData.amcExpiryDate;
      delete formData.warrantyExpiryDate;
    } else {
      formData.departmentId = selectedDepartment.id;
      delete formData.manufacturer;
    }

    formData.manufacturingYear = Number(formData.manufacturingYear);

    if (formData.id) {
      formDataToBe.append("machineDetails", JSON.stringify(formData));
      submitForm(updateMachine, formDataToBe);
    } else {
      formDataToBe.append("machineDetails", JSON.stringify(formData));
      submitForm(addMachine, formDataToBe);
    }
  };

  const submitForm = (action, formData) => {
    dispatch(action(formData)).then((machineRes) => {
      if (machineRes && machineRes.error) {
        toast.error("Something went wrong!");
      } else if (machineRes && machineRes.payload && machineRes.payload.data && machineRes.payload.status === 200) {
        if (!formData.machineId) {
          navigate(`/${Links.EDIT_MACHINE}/` + machineRes.payload.data.id, { replace: true });
        }

        dispatch(getMachines()).then((res) => {
          toast.success("Machine " + (formData.machineId ? "updated" : "added") + " successfully!", {
            autoClose: 1000,
          });

          props.setEnableEditing(false);
        });
      } else {
        toast.error("Something went wrong!");
      }
    });
  };

  if (isLoading) {
    return (
      <div
        style={{
          textAlign: "center",
          paddingTop: 200,
        }}
      >
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return (
    <div>
      <Stack gap={2}>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
          <Stack alignItems={"center"} sx={{ flex: 0.2 }}>
            <Card sx={{ borderRadius: 5 }}>
              <Stack alignItems={"center"} p={3} gap={2}>
                <Button startIcon={<FileUploadIcon />} component="label" sx={mode === "dark" ? { color: "white" } : {}}>
                  Upload
                  <input
                    style={{ display: "none" }}
                    hidden
                    accept="image/*"
                    multiple
                    type="file"
                    id="fileInput"
                    onChange={handleFileUpload}
                  />
                </Button>
                {imageToUpload ? (
                  <img src={URL.createObjectURL(imageToUpload)} alt="Selected File" style={{ maxWidth: "250px", maxHeight: "250px" }} />
                ) : formData.profileImage ? (
                  <AuthorizedImage path={formData.profileImage} width={200} height={200} />
                ) : null}
              </Stack>
            </Card>
          </Stack>
          <Stack flexWrap={"wrap"} sx={{ flex: 0.7 }}>
            <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                sx={{ width: '100%' }}
                disabled={isEditDisabled}
                label="Serial number"
                value={formData.serialNumber}
                onChange={(event) => setFormData({ ...formData, serialNumber: event.target.value })}
                onBlur={() =>
                  setFormData((prev) => ({
                    ...prev,
                    serialNumber: prev.serialNumber.trim(),
                  }))
                }
                error={!!(errors.serialNumber && !formData.serialNumber)}
                helperText={errors.serialNumber && !formData.serialNumber ? errors.serialNumber : ''}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <TextField
                sx={{ width: '100%' }}
                disabled={isEditDisabled}
                label="Manufacturing Year"
                value={formData.manufacturingYear}
                onChange={(event) => setFormData({ ...formData, manufacturingYear: event.target.value })}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <TextField
                sx={{ width: '100%' }}
                disabled={isEditDisabled}
                label="Maintenance Code"
                value={formData.maintenanceCode}
                onChange={(event) => setFormData({ ...formData, maintenanceCode: event.target.value })}
                onBlur={() =>
                  setFormData((prev) => ({
                    ...prev,
                    maintenanceCode: (prev.maintenanceCode|| '').trim(),
                  }))
                }
                error={!!(errors.maintenanceCode && !formData.maintenanceCode)}
                helperText={ errors.maintenanceCode && !formData.maintenanceCode? errors.maintenanceCode: ''}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <TextField
                sx={{ width: '100%' }}
                label="Asset Number"
                value={formData.assetNumber}
                onChange={(event) => setFormData({ ...formData, assetNumber: event.target.value })}
                onBlur={() =>
                  setFormData((prev) => ({
                    ...prev,
                    assetNumber: (prev.assetNumber || '').trim(),
                  }))
                }
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isEditDisabled}
                disablePortal
                id="manufacturer-dropdown"
                onChange={(event, newValue) =>
                  setFormData({ ...formData, manufacturer: newValue || '', model: '' })
                }
                value={formData.manufacturer || null}
                options={
                  Object.keys(appConstants).length
                    ? [...new Set(appConstants.machineModels.map((item) => item.manufacturer))].sort((a, b) => a.localeCompare(b))
                    : []
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manufacturer"
                    error={errors && errors.manufacturer && !formData.manufacturer}
                    helperText={errors.manufacturer}
                  />
                )}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isEditDisabled}
                disablePortal
                id="combo-box-demo"
                onChange={(event, newValue) =>
                  setFormData({
                    ...formData,
                    model: newValue.model,
                  })
                }
                isOptionEqualToValue={(option, value) => option.model === value.model}
                getOptionLabel={(option) => option.model}
                value={appConstants.machineModels.find((item) => item.model === formData.model) || null}
                options={
                  Object.keys(appConstants).length ? appConstants.machineModels
                        .filter((item) => item.manufacturer === formData.manufacturer)
                        .sort((a, b) => a.model.localeCompare(b.model))
                    : []
                }
                renderInput={(params) => (
                  <TextField {...params} label="Model" error={!!(errors.model && !formData.model)} helperText={errors.model && !formData.model ? errors.model : ''} />
                )}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isEditDisabled}
                disablePortal
                id="combo-box-demo"
                onChange={(event, newValue, reason) =>
                  setFormData({
                    ...formData,
                    machineType: reason === "clear" ? "" : newValue,
                  })
                }
                value={formData.machineType || null}
                options={
                  Object.keys(appConstants).length ? [...Object.values(appConstants?.machineType)].sort((a, b) => a.localeCompare(b)) : []
                }
                renderInput={(params) => <TextField {...params} label="Machine Type" />}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isEditDisabled}
                disablePortal
                id="combo-box-demo"
                onChange={(event, newValue) =>
                  setFormData({
                    ...formData,
                    location: newValue.name,
                  })
                }
                isOptionEqualToValue={(option, value) => option.name === value.name}
                getOptionLabel={(option) => option.name}
                value={machineDepartments.find((item) => item.name === formData.location) || null}
                options={
                  Object.keys(appConstants).length
                    ? machineDepartments
                        .map((item) => ({ ...item, value: item.name, label: item.name }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    : []
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    error={!!(errors.location && !formData.location)}
                    helperText={ errors.location && !formData.location ? errors.location : '' }
                  />
                )}
              />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isEditDisabled}
                      checked={formData.criticalMachine}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          criticalMachine: event.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    formData.criticalMachine
                      ? "Critical Machine (Uncheck the box to disable critical machine)"
                      : "Critical Machine (Check the box to enable critical machine)"
                  }
                  style={{ whiteSpace: "nowrap" }}
                />
              </Grid>
            </Grid>
          </Stack>
          <Stack sx={{ flex: 0.1 }} alignItems={"center"}>
            <Tooltip title="Save">
              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      <ToastContainer />
    </div>
  );
};

export default AddEditMachine;
